import { memo, useCallback, useEffect, useMemo, useState } from "react";
import Loading from "components/Loading";
import AutoSizer from "react-virtualized-auto-sizer";
import InfiniteLoader from "react-window-infinite-loader";
import useFetch from "utils/hooks/useFetch";
import RenderListItem from "./RenderListItem";
import { docListStatus, updateTotalFilteredDocs } from "utils/redux/actions";
import { FixedSizeList } from "react-window";
import { connect } from "react-redux";
import AlertMessage from "components/AlertMessage";
import Page404 from "components/Page404";

const RenderList = ({
  dispatch,
  serviceID,
  serviceType,
  status,
  documentsList,
  setDocumentsList,
  displayReference,
  toggleModal,
  setConfirmDelete,
  setConfirmCancel,
  setDocument,
  setConfirmRelease,
  mainModule,
  filterByName = "",
  filterByEnvelopeName = "",
  signatureEditor,
  filterByReference = "",
  listChange,
}) => {
  const requestChunksData = useMemo(
    () => ({
      serviceID,
      serviceType,
      status,
      filterByName,
      filterByEnvelopeName,
      filterByReference,
    }),
    [
      serviceID,
      serviceType,
      status,
      filterByName,
      filterByEnvelopeName,
      filterByReference,
      setConfirmDelete,
      setConfirmCancel,
      listChange,
    ]
  );

  const {
    status: fetchDocStatus,
    data: chunks,
    fetchData: fetchChunks,
  } = useFetch({
    method: "POST",
    url: "/documents/load-documents",
    immediate: false,
  });
  const {
    status: updatesStatus,
    data: updatedDocuments,
    fetchData: fetchForUpdates,
  } = useFetch({
    method: "POST",
    url: "/documents/check-for-updates",
    immediate: false,
  });

  const isItemLoaded = useCallback(
    (index) => !!documentsList?.[index],
    [documentsList]
  );

  const loadMoreItems = useCallback(
    (loadedDocuments) => {
      const lastLoadedDocument = documentsList[--loadedDocuments].documentId;

      return fetchChunks({
        data: { ...requestChunksData, lastLoadedDocument },
      });
    },
    [documentsList, requestChunksData, fetchChunks]
  );

  const [itemCount, setItemCount] = useState(() => 0);
  const isApproved = useMemo(() => Boolean(+status), [status]);

  const itemData = useMemo(
    () => ({
      documentsList,
      serviceID,
      serviceType,
      displayReference,
      toggleModal,
      setConfirmDelete,
      setConfirmCancel,
      setDocument,
      setConfirmRelease,
      mainModule,
      isApproved,
      signatureEditor: signatureEditor,
    }),
    [
      documentsList,
      serviceID,
      serviceType,
      displayReference,
      toggleModal,
      setConfirmDelete,
      setDocument,
      setConfirmRelease,
      mainModule,
      isApproved,
      signatureEditor,
    ]
  );

  const itemKey = useCallback(
    (index, { documentsList }) => documentsList?.[index]?.documentId,
    []
  );

  useEffect(() => {
    if (!chunks) return;
    const { docs, totalActive, totalDocs, totalDocsAfterFilter } = chunks;

    if (docs?.length) setDocumentsList((oldList) => [...oldList, ...docs]);
    if ("totalActive" in chunks) {
      dispatch(docListStatus({ [serviceID]: totalActive }));
    }
    const hasFilter = Boolean(
      (filterByName && filterByName.trim()) ||
        (filterByEnvelopeName && filterByEnvelopeName.trim()) ||
        (filterByReference && filterByReference.trim())
    );

    const shownCount =
      typeof totalDocsAfterFilter === "number" && hasFilter
        ? totalDocsAfterFilter
        : totalDocs;

    if (typeof shownCount === "number") {
      setItemCount(shownCount);
      dispatch(updateTotalFilteredDocs(shownCount));
    }
  }, [chunks, setDocumentsList, dispatch, serviceID]);

  useEffect(() => {
    /**
     * If request data is changed e.g. filterByName.
     * Reset documentsList.
     * Refetch data.
     */

    fetchChunks({ data: requestChunksData });

    return () => {
      dispatch(updateTotalFilteredDocs());
      setDocumentsList([]);
    };
  }, [fetchChunks, requestChunksData, setDocumentsList, dispatch]);

  useEffect(() => {
    /**
     * Check DB for new documents or updated documents(only unfinished).
     */
    let checkForUpdates;

    if (isApproved) return; //dont check for approved documents
    if (fetchDocStatus !== "fetched") return; //dont check if fetching is in progress
    if (updatesStatus === "fetching") return; //dont check if checking is in progress

    const CHECK_INTERVAL = 10_000; //in ms
    const listToCheck = documentsList
      .filter(({ thumbnailUrl }, index) => index === 0 || !thumbnailUrl)
      .map(({ documentId, updatedAt }) => ({ documentId, updatedAt }));

    const data = {
      ...requestChunksData,
      listToCheck,
    };

    checkForUpdates = setInterval(
      () => fetchForUpdates({ data }),
      CHECK_INTERVAL
    );

    return () => (checkForUpdates ? clearInterval(checkForUpdates) : null);
  }, [
    isApproved,
    fetchDocStatus,
    updatesStatus,
    documentsList,
    requestChunksData,
    fetchForUpdates,
  ]);

  useEffect(() => {
    if (updatesStatus !== "fetched") return;

    const {
      docs,
      totalActive,
      totalDocs,
      totalDocsAfterFilter,
      missingDocIds,
    } = updatedDocuments;

    if (
      !docs ||
      (!docs.length && (!missingDocIds || missingDocIds?.length === 0))
    )
      return;

    if ("totalActive" in updatedDocuments) {
      dispatch(docListStatus({ [serviceID]: totalActive }));
    }
    const hasFilter = Boolean(
      (filterByName && filterByName.trim()) ||
        (filterByEnvelopeName && filterByEnvelopeName.trim()) ||
        (filterByReference && filterByReference.trim())
    );

    const shownCount =
      typeof totalDocsAfterFilter === "number" && hasFilter
        ? totalDocsAfterFilter
        : totalDocs;

    if (typeof shownCount === "number") {
      setItemCount(shownCount);
      dispatch(updateTotalFilteredDocs(shownCount));
    }

    const REDUCER = (arr, document, documentIndex) => {
      const actualId = document.documentId;
      const allIds = arr.map(({ documentId }) => documentId);
      const isDocumentIncluded = allIds.includes(actualId);

      let leftPart, rightPart, separationIndex;

      if (isDocumentIncluded) {
        separationIndex = allIds.indexOf(actualId);
        leftPart = arr.slice(0, separationIndex);
        rightPart = arr.slice(++separationIndex, arr.length); //skip one index to overwrite the current document
      } else {
        separationIndex = documentIndex;
        leftPart = arr.slice(0, separationIndex);
        rightPart = arr.slice(separationIndex, arr.length);
      }

      return [...leftPart, document, ...rightPart];
    };

    setDocumentsList((oldArr) => {
      const updatedArr = updatedDocuments.docs.reduce(REDUCER, oldArr);
      const filteredArr = missingDocIds
        ? updatedArr.filter(
            ({ documentId }) => !missingDocIds.includes(documentId)
          )
        : updatedArr;
      return filteredArr;
    });
  }, [dispatch, serviceID, updatesStatus, updatedDocuments, setDocumentsList]);

  if (fetchDocStatus === "error") return <Page404 height="100%" />;

  if (fetchDocStatus !== "fetched" && !documentsList?.length)
    return <Loading classes="h-100" />;

  if (!documentsList?.length)
    return (
      <AlertMessage
        classes="mt-3"
        alertMessage="Keine Daten verfügbar!"
        background="info"
      />
    );

  return (
    <AutoSizer>
      {({ height, width }) => (
        <InfiniteLoader
          isItemLoaded={isItemLoaded}
          itemCount={itemCount}
          loadMoreItems={loadMoreItems}
          threshold={10}
        >
          {({ onItemsRendered, ref }) => (
            <FixedSizeList
              ref={ref}
              onItemsRendered={onItemsRendered}
              width={width}
              height={height}
              itemSize={400}
              itemCount={documentsList.length}
              itemData={itemData}
              itemKey={itemKey}
              children={RenderListItem}
            />
          )}
        </InfiniteLoader>
      )}
    </AutoSizer>
  );
};

const mapStateToProps = ({
  filterByName,
  filterByEnvelopeName,
  filterByReference,
}) => ({ filterByName, filterByEnvelopeName, filterByReference });

export default connect(mapStateToProps)(memo(RenderList));
